/**
 * Browser-based Ed25519 device identity for Gateway authentication.
 * Uses Web Crypto API (Ed25519 supported in Chrome 113+, Safari 17+, Firefox 130+).
 * Mirrors equabot-gateway/ui/src/ui/device-identity.ts but uses Web Crypto instead of @noble/ed25519.
 */

type StoredIdentity = {
  version: 2
  deviceId: string
  publicKeyBase64Url: string
  privateKeyJwk: JsonWebKey
  createdAtMs: number
}

export type DeviceIdentity = {
  deviceId: string
  publicKeyBase64Url: string
  privateKey: CryptoKey
}

const STORAGE_KEY = 'equabot-device-identity-v2'

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

async function fingerprintPublicKeyRaw(publicKeyRaw: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', publicKeyRaw)
  return bytesToHex(new Uint8Array(hash))
}

async function importPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'Ed25519' } as Algorithm, false, ['sign'])
}

async function generateIdentity(): Promise<DeviceIdentity & { privateKeyJwk: JsonWebKey }> {
  const keyPair = await crypto.subtle.generateKey({ name: 'Ed25519' } as Algorithm, true, [
    'sign',
    'verify',
  ]) as CryptoKeyPair

  const publicKeyRaw = await crypto.subtle.exportKey('raw', keyPair.publicKey)
  const privateKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey)
  const publicKeyBase64Url = arrayBufferToBase64Url(publicKeyRaw)
  const deviceId = await fingerprintPublicKeyRaw(publicKeyRaw)

  return { deviceId, publicKeyBase64Url, privateKey: keyPair.privateKey, privateKeyJwk }
}

export async function loadOrCreateDeviceIdentity(): Promise<DeviceIdentity> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as StoredIdentity
      if (
        parsed?.version === 2 &&
        typeof parsed.deviceId === 'string' &&
        typeof parsed.publicKeyBase64Url === 'string' &&
        parsed.privateKeyJwk
      ) {
        const privateKey = await importPrivateKey(parsed.privateKeyJwk)
        return {
          deviceId: parsed.deviceId,
          publicKeyBase64Url: parsed.publicKeyBase64Url,
          privateKey,
        }
      }
    }
  } catch {
    // fall through to regenerate
  }

  const identity = await generateIdentity()
  const stored: StoredIdentity = {
    version: 2,
    deviceId: identity.deviceId,
    publicKeyBase64Url: identity.publicKeyBase64Url,
    privateKeyJwk: identity.privateKeyJwk,
    createdAtMs: Date.now(),
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // best-effort
  }
  return { deviceId: identity.deviceId, publicKeyBase64Url: identity.publicKeyBase64Url, privateKey: identity.privateKey }
}

export async function signDevicePayload(privateKey: CryptoKey, payload: string): Promise<string> {
  const data = new TextEncoder().encode(payload)
  const sig = await crypto.subtle.sign({ name: 'Ed25519' } as Algorithm, privateKey, data)
  return arrayBufferToBase64Url(sig)
}

/**
 * Build the pipe-separated payload string for device auth signing.
 * Must match equabot-gateway/src/gateway/device-auth.ts:buildDeviceAuthPayload exactly.
 */
export function buildDeviceAuthPayload(params: {
  deviceId: string
  clientId: string
  clientMode: string
  role: string
  scopes: string[]
  signedAtMs: number
  token?: string | null
  nonce?: string | null
}): string {
  const version = params.nonce ? 'v2' : 'v1'
  const scopes = params.scopes.join(',')
  const token = params.token ?? ''
  const base = [
    version,
    params.deviceId,
    params.clientId,
    params.clientMode,
    params.role,
    scopes,
    String(params.signedAtMs),
    token,
  ]
  if (version === 'v2') {
    base.push(params.nonce ?? '')
  }
  return base.join('|')
}

export function isSecureContext(): boolean {
  return typeof crypto !== 'undefined' && !!crypto.subtle
}
